/**
 * THE AUDIO LANE'S OWN RANDOM SOURCE, and it is deliberately not the simulation's.
 *
 * `src/sim/rng.ts` is a counter-addressed generator whose contract is that a tick index and a
 * channel name address one draw for all time; a dozen fences in that lane rest on nobody else
 * consuming from it. Sound needs a different shape entirely - a long unbroken stream of white
 * noise, millions of draws with no index worth naming - so it gets its own generator rather
 * than borrowing a stream that other tests assert the position of.
 *
 * IT IS SEEDED AND IT IS FIXED. Every buffer in this lane is generated from a constant seed
 * written in `spec.ts`, never from the save's seed and never from the clock, so:
 *
 *   - every player hears the same sea, which is what makes it a designed object rather than a
 *     property of their machine;
 *   - regenerating the artifacts twice produces byte-identical Float32 output, which is what
 *     the run's hash-pinning needs;
 *   - a defect in the sea is reproducible from one number in a source file.
 *
 * The algorithm is sfc32, chosen because it passes PractRand well past the ~2 million draws a
 * sea layer needs and costs four adds, two shifts and a rotate per draw. Its period is at
 * least 2^32 and typically ~2^127, both far beyond one buffer. Nothing here is cryptographic
 * and nothing here needs to be.
 */

/** A stream of uniform draws. Stateful, so callers hold one and pull from it. */
export interface Rng {
  /** Uniform in [0, 1). */
  next(): number;
  /** Uniform in [-1, 1). White noise, which is what every generator here actually wants. */
  bipolar(): number;
}

/**
 * SplitMix32, used ONLY to expand one seed into the four words sfc32 needs.
 *
 * Seeding sfc32 with the same number in all four slots gives a badly correlated first few
 * hundred draws; expanding through a different mixer first is the standard fix and costs
 * four calls once per buffer.
 */
function splitmix32(seed: number): () => number {
  let a = seed >>> 0;
  return (): number => {
    a = (a + 0x9e3779b9) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
    t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
    return (t ^ (t >>> 15)) >>> 0;
  };
}

/** A fresh stream. The same seed always gives the same stream, on every machine. */
export function makeRng(seed: number): Rng {
  const mix = splitmix32(seed >>> 0);
  let a = mix();
  let b = mix();
  let c = mix();
  let d = mix();
  const next = (): number => {
    a >>>= 0;
    b >>>= 0;
    c >>>= 0;
    d >>>= 0;
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
  return { next, bipolar: () => next() * 2 - 1 };
}

/**
 * A 32-bit hash of a string, so an event can name its own variant slot.
 *
 * FNV-1a. Used to turn "a mackerel, the 41st one landed" into a repeatable choice of which
 * pre-rendered variant plays and how far it is detuned - so two players who land the same
 * fish on the same save hear the same thing, and a screenshot script can assert it.
 */
export function hashText(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
